import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Loader2, QrCode, Truck, ExternalLink, Tag, FileDown, Plus } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { generateQRStickerZip } from "@/lib/qr-label-zip";
import JSZip from "jszip";

interface BoxWithLabels {
  id: string;
  box_number: string;
  name: string;
  shipping_label_url: string | null;
  tracking_number: string | null;
  easypost_tracking_url: string | null;
  label_generated_at: string | null;
}

interface UserAddress {
  full_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string;
}

interface UserLabelsSectionProps {
  userId: string;
  userName: string | null;
  userAddress: UserAddress;
  boxes: BoxWithLabels[];
  onLabelsGenerated?: () => void;
}

export const UserLabelsSection = ({
  userId,
  userName,
  userAddress,
  boxes,
  onLabelsGenerated,
}: UserLabelsSectionProps) => {
  const [generatingQR, setGeneratingQR] = useState(false);
  const [generatingShipping, setGeneratingShipping] = useState(false);
  const [generatingSingleId, setGeneratingSingleId] = useState<string | null>(null);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const boxesWithLabels = boxes.filter((b) => b.shipping_label_url);
  const boxesWithoutLabels = boxes.filter((b) => !b.shipping_label_url);

  const handleGenerateQR = async () => {
    if (boxes.length === 0) { toast.error("No boxes"); return; }
    setGeneratingQR(true);
    try {
      const { blob, elapsed } = await generateQRStickerZip(boxes);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = `qr-stickers-${userId.slice(0, 8)}.zip`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Generated ${boxes.length} QR stickers in ${elapsed}s`);
    } catch (error) {
      console.error("QR generation error:", error);
      toast.error("Failed to generate QR stickers");
    } finally {
      setGeneratingQR(false);
    }
  };

  const handleGenerateMissing = async () => {
    if (boxesWithoutLabels.length === 0) { toast.info("All boxes already have labels"); return; }
    if (!userAddress.address_line1) { toast.error("User has no address on file"); return; }

    setGeneratingShipping(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke(
        "generate-prepaid-shipping-labels",
        {
          body: {
            user_id: userId,
            boxes: boxesWithoutLabels.map((b) => ({ id: b.id, box_number: b.box_number })),
            kit_size: "large",
            kit_count: 1,
            user_address: {
              full_name: userAddress.full_name || "",
              address_line1: userAddress.address_line1,
              address_line2: userAddress.address_line2 || "",
              city: userAddress.city || "",
              state: userAddress.state || "",
              zip: userAddress.zip || "",
              phone: userAddress.phone,
            },
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        }
      );

      if (error) throw error;
      if (!data.success) throw new Error(data.error || "Failed to generate labels");

      const msg = [
        `${data.return_label_count || 0} labels generated`,
        data.skipped_existing ? `${data.skipped_existing} skipped (already exist)` : null,
        data.failed_boxes?.length ? `${data.failed_boxes.length} failed` : null,
      ].filter(Boolean).join(", ");

      toast.success(msg);
      onLabelsGenerated?.();
    } catch (error: any) {
      console.error("Shipping label error:", error);
      toast.error(error.message || "Failed to generate shipping labels");
    } finally {
      setGeneratingShipping(false);
    }
  };

  const handleGenerateSingle = async (box: BoxWithLabels) => {
    if (!userAddress.address_line1) { toast.error("User has no address on file"); return; }

    setGeneratingSingleId(box.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke(
        "generate-single-return-label",
        {
          body: {
            box_id: box.id,
            user_address: {
              full_name: userAddress.full_name || "",
              address_line1: userAddress.address_line1,
              address_line2: userAddress.address_line2 || "",
              city: userAddress.city || "",
              state: userAddress.state || "",
              zip: userAddress.zip || "",
              phone: userAddress.phone,
            },
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        }
      );

      if (error) throw error;
      if (!data.success) throw new Error(data.error || "Failed");

      if (data.already_exists) {
        toast.info(`Box #${box.box_number} already has a label`);
      } else {
        toast.success(`Label generated for box #${box.box_number}`);
      }
      onLabelsGenerated?.();
    } catch (error: any) {
      console.error("Single label error:", error);
      toast.error(error.message || "Failed to generate label");
    } finally {
      setGeneratingSingleId(null);
    }
  };

  const getFileExtension = (url: string): string => {
    try {
      const pathname = new URL(url).pathname;
      if (pathname.endsWith(".png")) return ".png";
      if (pathname.endsWith(".pdf")) return ".pdf";
    } catch {}
    return ".pdf";
  };

  const handleDownloadAllLabels = async () => {
    if (boxesWithLabels.length === 0) { toast.error("No labels to download"); return; }

    setDownloadingZip(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const zip = new JSZip();
      let added = 0;

      for (const box of boxesWithLabels) {
        try {
          const response = await fetch(
            `${supabaseUrl}/functions/v1/proxy-label-download`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${session.access_token}`,
                apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
              },
              body: JSON.stringify({ url: box.shipping_label_url }),
            }
          );

          if (!response.ok) {
            console.warn(`Failed to fetch label for box ${box.box_number}: ${response.status}`);
            continue;
          }

          const buffer = await response.arrayBuffer();
          const ext = getFileExtension(box.shipping_label_url!);
          zip.file(`return-${box.box_number}${ext}`, buffer);
          added++;
        } catch (e) {
          console.warn(`Error downloading label for box ${box.box_number}:`, e);
        }
      }

      if (added === 0) {
        toast.error("Could not download any labels");
        return;
      }

      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = `shipping-labels-${userId.slice(0, 8)}.zip`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${added} labels`);
    } catch (error) {
      console.error("ZIP download error:", error);
      toast.error("Failed to create ZIP");
    } finally {
      setDownloadingZip(false);
    }
  };

  return (
    <div>
      <h3 className="font-semibold mb-3 flex items-center gap-2">
        <Tag className="h-4 w-4" />
        Labels & Shipping
      </h3>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 text-sm mb-4">
        <div>
          <span className="text-muted-foreground">Labels Generated:</span>
          <p className="font-medium text-lg">{boxesWithLabels.length} / {boxes.length}</p>
        </div>
        <div>
          <span className="text-muted-foreground">Missing Labels:</span>
          <p className="font-medium text-lg">{boxesWithoutLabels.length}</p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button size="sm" variant="outline" onClick={handleGenerateQR} disabled={generatingQR || boxes.length === 0}>
          {generatingQR ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <QrCode className="h-4 w-4 mr-1" />}
          QR Stickers
        </Button>

        {boxesWithoutLabels.length > 0 && (
          <Button size="sm" variant="outline" onClick={handleGenerateMissing} disabled={generatingShipping}>
            {generatingShipping ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Truck className="h-4 w-4 mr-1" />}
            Generate {boxesWithoutLabels.length} Missing Label{boxesWithoutLabels.length !== 1 ? "s" : ""}
          </Button>
        )}

        {boxesWithLabels.length > 0 && (
          <Button size="sm" variant="outline" onClick={handleDownloadAllLabels} disabled={downloadingZip}>
            {downloadingZip ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileDown className="h-4 w-4 mr-1" />}
            Download All Labels ({boxesWithLabels.length})
          </Button>
        )}
      </div>

      {/* Per-box label status */}
      {boxes.length > 0 && (
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {boxes.map((box) => (
            <div key={box.id} className="flex items-center justify-between text-xs p-2 border rounded">
              <div className="flex items-center gap-2">
                <span className="font-mono font-medium">#{box.box_number}</span>
                <span className="text-muted-foreground truncate max-w-[120px]">{box.name}</span>
              </div>
              <div className="flex items-center gap-1.5">
                {box.shipping_label_url ? (
                  <>
                    <Badge variant="default" className="text-[10px] px-1.5 py-0">Label</Badge>
                    <Button
                      size="icon" variant="ghost" className="h-5 w-5"
                      onClick={() => window.open(box.shipping_label_url!, "_blank")}
                      title="Download label"
                    >
                      <Download className="h-3 w-3" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">No label</Badge>
                    <Button
                      size="icon" variant="ghost" className="h-5 w-5"
                      onClick={() => handleGenerateSingle(box)}
                      disabled={generatingSingleId === box.id || generatingShipping}
                      title="Generate label for this box"
                    >
                      {generatingSingleId === box.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Plus className="h-3 w-3" />
                      )}
                    </Button>
                  </>
                )}
                {box.easypost_tracking_url && (
                  <Button
                    size="icon" variant="ghost" className="h-5 w-5"
                    onClick={() => window.open(box.easypost_tracking_url!, "_blank")}
                    title="Track shipment"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
